import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { api } from '../services/api';

const dash = (value) => (value === 0 || value === undefined || value === null ? '—' : value);

const rssiColor = (value) => {
  if (!value) return 'text.primary';
  if (value >= -50) return 'error.main';
  if (value >= -70) return 'warning.main';
  return 'text.primary';
};

const loadColor = (value) => {
  if (!value) return 'text.primary';
  if (value >= 70) return 'error.main';
  if (value >= 40) return 'warning.main';
  return 'text.primary';
};

const sortedChannels = (channels) => (
  [...(channels || [])].sort((a, b) => (a.channel || 0) - (b.channel || 0))
);

const AcsSurveyDialog = ({ open, serial, onClose }) => {
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !serial) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSurvey(null);
    api.getDevice(serial, { includeAcsSurvey: true })
      .then((device) => {
        if (!cancelled) setSurvey(device?.acs_survey || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load ACS survey');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, serial]);

  const radios = [...(survey?.radios || [])].sort((a, b) => String(a.label).localeCompare(String(b.label)));
  const received = survey?.received_at ? new Date(survey.received_at).toLocaleString() : '';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        ACS channel survey
        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {serial}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && error && (
          <Typography color="error">{error}</Typography>
        )}
        {!loading && !error && !survey && (
          <Typography color="text.secondary">This device has not reported an ACS survey.</Typography>
        )}
        {!loading && !error && survey && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {received ? `Received ${received}` : 'Received time unknown'}
              {survey.source ? ` · ${survey.source}` : ''}
            </Typography>
            {radios.length === 0 && (
              <Typography color="text.secondary">The survey has no channel samples.</Typography>
            )}
            {radios.map((radio) => (
              <Box key={radio.label || radio.band} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  {radio.label || 'Radio'}
                  {radio.band ? ` · ${radio.band}` : ''}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Channel', 'MHz', 'BSS', 'Max RSSI', 'Min RSSI', 'Noise', 'Load %', 'Avail %', 'Grade', 'Radar'].map((heading) => (
                        <TableCell key={heading} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{heading}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedChannels(radio.channels).map((entry) => (
                      <TableRow key={`${radio.label}-${entry.channel}-${entry.frequency}`}>
                        <TableCell>{entry.channel}</TableCell>
                        <TableCell>{entry.frequency || '—'}</TableCell>
                        <TableCell>{entry.bss}</TableCell>
                        <TableCell sx={{ color: rssiColor(entry.max_rssi), fontWeight: entry.max_rssi ? 600 : 400 }}>
                          {dash(entry.max_rssi)}
                        </TableCell>
                        <TableCell>{dash(entry.min_rssi)}</TableCell>
                        <TableCell>{dash(entry.noise_floor)}</TableCell>
                        <TableCell sx={{ color: loadColor(entry.ch_load), fontWeight: entry.ch_load >= 40 ? 600 : 400 }}>
                          {entry.ch_load}
                        </TableCell>
                        <TableCell>{entry.ch_avail}</TableCell>
                        <TableCell>{entry.grade}</TableCell>
                        <TableCell>{entry.radar ? 'Yes' : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AcsSurveyDialog;
